/**
 * Microphone capture → PCM16 → edge WebSocket.
 *
 * Written as a plain class rather than a hook because the interesting part is
 * a lifecycle with several failure modes, and a class can be unit-tested
 * without mounting a component.
 *
 * Two behaviours here are load-bearing and were each a shipped bug fix:
 *
 * 1. The worklet is routed through a muted gain node into destination.
 *    Firefox may not pull from an AudioWorklet that isn't connected to an
 *    output, so without this the mic appears to start and silently sends
 *    nothing.
 * 2. Start does not resolve until an audio frame has actually been produced.
 *    Permission being granted is not proof that frames flow, and reporting
 *    "listening" to a GM who is not being heard is the worst failure this
 *    component has.
 */

export type MicState = 'off' | 'starting' | 'on';

export interface MicIncomingMessage {
  type: 'partial' | 'turn' | 'audio_status';
  text?: string;
  message?: string;
  level?: string;
  /** False when the turn reached the browser but not the database — the GM
   *  sees the caption and the agent never sees the line. */
  persisted?: boolean;
  [key: string]: unknown;
}

export interface MicrophoneCallbacks {
  onState: (state: MicState) => void;
  onMessage: (message: MicIncomingMessage) => void;
  onLog: (message: string, level?: 'info' | 'ok' | 'warn') => void;
}

/**
 * Where the audio comes from.
 *
 * `microphone` is a table in one room. The other two are for a table that is
 * partly or wholly remote:
 *
 * - `device` targets a specific input, which is how a virtual audio cable
 *   (BlackHole, VB-Cable, Loopback) gets Discord's output into Throughline.
 *   The GM routes Discord to the cable and picks the cable here.
 * - `display` uses getDisplayMedia to capture a tab or window's audio, so a
 *   browser-based Discord call can be captured with no extra software. The
 *   video track it forces on us is dropped immediately.
 *
 * Either way the pipeline downstream is identical — a MediaStream is a
 * MediaStream.
 */
export type AudioSourceKind = 'microphone' | 'device' | 'display';

export interface AudioSource {
  kind: AudioSourceKind;
  /** deviceId, for kind === 'device'. */
  deviceId?: string;
  label?: string;
}

export interface MicrophoneDeps {
  getStreamToken: () => Promise<string>;
  /** Chosen input. Defaults to the system microphone. */
  source?: AudioSource;
  getDisplayMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  /** Injected for tests; defaults to the browser globals. */
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  createAudioContext?: () => AudioContext;
  createWebSocket?: (url: string) => WebSocket;
  /** Overridable so tests can run on real timers with tiny values. */
  firstFrameTimeoutMs?: number;
  reconnectBaseMs?: number;
  frameWatchdogMs?: number;
}

const WORKLET_URL = '/static/pcm16-worklet.js';
/** Non-audio message the worklet posts on its port (see pcm16-worklet.js). */
interface WorkletNotice {
  type: 'rate';
  contextRate: number;
  ratio: number;
}

const TARGET_SAMPLE_RATE = 16_000;
const CAPTURE_SAMPLE_RATE = 48_000;
const FIRST_FRAME_TIMEOUT_MS = 3_000;
const MAX_RECONNECT_MS = 10_000;

/**
 * How long capture may go without producing a frame before we treat it as
 * broken. The worklet flushes every 100ms, so several seconds of nothing is
 * unambiguous — and a recent frame is the *only* evidence that actually
 * proves capture is alive. Everything else (socket open, track live, state
 * "on") can all be true while no audio is reaching the server, which is
 * exactly the failure GMs reported as "sometimes it just wasn't recording".
 */
const FRAME_WATCHDOG_MS = 5_000;

/**
 * Stop sending when the socket has this much unflushed. Audio is realtime;
 * a frame that arrives late is worthless, so dropping beats growing a buffer
 * that will only ever deliver stale audio.
 */
const MAX_BUFFERED_BYTES = 1_000_000;

export class MicrophoneSession {
  private state: MicState = 'off';
  private desired = false;
  // Whether a start() is currently running. Kept separate from `state`:
  // `state` is what the button shows, and a pending reconnect displays
  // "starting" while no start is in flight. Guarding start() on the display
  // state made every scheduled reconnect return immediately.
  private inFlight = false;
  private reconnectAttempt = 0;
  private reconnectTimer?: number;
  private watchdogTimer?: number;
  private lastFrameAt = 0;
  private droppedForBackpressure = 0;

  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private socket: WebSocket | null = null;

  constructor(
    private readonly sessionId: string,
    private readonly callbacks: MicrophoneCallbacks,
    private readonly deps: MicrophoneDeps,
  ) {}

  get current(): MicState {
    return this.state;
  }

  async toggle(): Promise<void> {
    if (this.state === 'starting') return;
    if (this.state === 'on') {
      await this.stop();
      return;
    }
    await this.start();
  }

  async start({ reconnect = false } = {}): Promise<boolean> {
    if (this.inFlight || this.state === 'on') return this.state === 'on';

    this.inFlight = true;
    this.desired = true;
    this.setState('starting');

    let stream: MediaStream | null = null;
    let context: AudioContext | null = null;
    let socket: WebSocket | null = null;

    try {
      stream = await this.captureStream();

      context =
        this.deps.createAudioContext?.() ??
        new AudioContext({ sampleRate: CAPTURE_SAMPLE_RATE });
      await context.audioWorklet.addModule(WORKLET_URL);

      const source = context.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(context, 'pcm16-worklet', {
        processorOptions: { targetSampleRate: TARGET_SAMPLE_RATE },
      });

      // See (1) in the module docstring — Firefox needs a path to output.
      const keepalive = context.createGain();
      keepalive.gain.value = 0;
      source.connect(node);
      node.connect(keepalive);
      keepalive.connect(context.destination);

      if (context.state === 'suspended') {
        await context.resume().catch(() => undefined);
      }

      socket = await this.openSocket();
      this.socket = socket;

      const firstFrame = this.awaitFirstFrame(node, socket);
      await firstFrame;

      this.stream = stream;
      this.context = context;
      this.node = node;
      this.reconnectAttempt = 0;
      this.clearReconnectTimer();
      this.watchHardware(stream, context);
      this.startWatchdog();
      this.setState('on');
      this.callbacks.onLog(reconnect ? 'mic reconnected' : 'mic on', 'ok');
      this.inFlight = false;
      return true;
    } catch (error) {
      await disposeAll(socket, context, stream);
      this.stream = null;
      this.context = null;
      this.socket = null;

      // Cleared before scheduling, so the queued reconnect can actually run.
      this.inFlight = false;
      const message = error instanceof Error ? error.message : String(error);
      if (this.desired && reconnect) {
        this.scheduleReconnect(message);
      } else {
        this.desired = false;
        this.setState('off');
        this.callbacks.onLog(`mic failed: ${message}`, 'warn');
      }
      return false;
    }
  }

  async stop({ log = true } = {}): Promise<void> {
    this.desired = false;
    this.inFlight = false;
    this.reconnectAttempt = 0;
    this.clearReconnectTimer();
    this.clearWatchdog();

    const { socket, context, stream, node } = this;
    this.socket = null;
    this.context = null;
    this.stream = null;
    this.node = null;

    // Up to 100ms sits unflushed in the worklet's buffer — usually the tail of
    // the last word spoken. Ask for it before the socket goes away.
    try {
      node?.port.postMessage('flush');
    } catch {
      /* worklet already torn down */
    }

    await disposeAll(socket, context, stream);
    this.setState('off');
    if (log) this.callbacks.onLog('mic off');
  }

  /** Release everything without attempting to reconnect. */
  async dispose(): Promise<void> {
    await this.stop({ log: false });
  }

  // -- internals ------------------------------------------------------------
  /** Acquire a stream from whichever source the GM selected. */
  private async captureStream(): Promise<MediaStream> {
    const source = this.deps.source ?? { kind: 'microphone' as const };

    if (source.kind === 'display') {
      const getDisplayMedia =
        this.deps.getDisplayMedia ??
        ((c: MediaStreamConstraints) => navigator.mediaDevices.getDisplayMedia(c));
      // Chrome only offers tab/window audio when video is also requested,
      // even though we discard it.
      const stream = await getDisplayMedia({ video: true, audio: true });
      for (const video of stream.getVideoTracks?.() ?? []) {
        video.stop();
        stream.removeTrack?.(video);
      }
      if ((stream.getAudioTracks?.() ?? []).length === 0) {
        throw new Error(
          'That share had no audio. Re-share and tick "Share tab audio".',
        );
      }
      return stream;
    }

    const getUserMedia =
      this.deps.getUserMedia ??
      ((c: MediaStreamConstraints) => navigator.mediaDevices.getUserMedia(c));

    // A virtual cable carrying a Discord mix is already processed and often
    // multi-speaker; browser voice processing degrades it, so it is only
    // applied to a real microphone.
    const isMic = source.kind === 'microphone';
    return getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: isMic,
        noiseSuppression: isMic,
        autoGainControl: isMic,
        ...(source.deviceId ? { deviceId: { exact: source.deviceId } } : {}),
      },
    });
  }

  private setState(state: MicState): void {
    this.state = state;
    this.callbacks.onState(state);
  }

  private async openSocket(): Promise<WebSocket> {
    const token = await this.deps.getStreamToken();
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url =
      `${proto}://${location.host}/api/sessions/${encodeURIComponent(this.sessionId)}` +
      `/audio?stream_token=${encodeURIComponent(token)}`;

    const socket = this.deps.createWebSocket?.(url) ?? new WebSocket(url);
    socket.binaryType = 'arraybuffer';

    socket.onmessage = (event: MessageEvent<string>) => {
      try {
        this.callbacks.onMessage(JSON.parse(event.data) as MicIncomingMessage);
      } catch {
        /* a malformed frame must not tear down the stream */
      }
    };

    return new Promise<WebSocket>((resolve, reject) => {
      let opened = false;
      socket.onopen = () => {
        opened = true;
        resolve(socket);
      };
      socket.onerror = () => {
        if (!opened) reject(new Error('audio websocket failed'));
      };
      socket.onclose = (event: CloseEvent) => {
        const reason = `${event.code} ${event.reason ?? ''}`.trim();
        if (!opened) {
          reject(new Error(`audio websocket closed: ${reason}`));
          return;
        }
        // Closed after a healthy open: drop local resources and, if the GM
        // still wants the mic on, come back.
        if (this.socket === socket) {
          this.clearWatchdog();
          this.socket = null;
          void disposeAll(null, this.context, this.stream);
          this.context = null;
          this.stream = null;
          this.setState('off');
          if (this.desired) this.scheduleReconnect(reason);
        }
      };
    });
  }

  /** Resolve on the first PCM frame; reject if none arrives. See (2) above. */
  private awaitFirstFrame(node: AudioWorkletNode, socket: WebSocket): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let seen = false;
      const timer = window.setTimeout(() => {
        if (seen) return;
        reject(
          new Error(
            'The microphone was allowed, but this browser delivered no audio. ' +
              'Try turning the mic on again, or use Chrome.',
          ),
        );
      }, this.deps.firstFrameTimeoutMs ?? FIRST_FRAME_TIMEOUT_MS);

      node.port.onmessage = (event: MessageEvent<ArrayBuffer | WorkletNotice>) => {
        // The worklet reports the context's real rate once at startup, before
        // any audio. `new AudioContext({sampleRate})` is only a request —
        // Safari and iOS often ignore it — and a surprising rate is worth
        // seeing in the logs even though the worklet now resamples correctly
        // for any input rate.
        if (!(event.data instanceof ArrayBuffer)) {
          const notice = event.data;
          if (notice?.type === 'rate') {
            if (!notice.ratio) {
              this.callbacks.onLog(
                `audio context reported an unusable sample rate ` +
                  `(${notice.contextRate}Hz); no audio will be sent`,
                'warn',
              );
            } else if (notice.contextRate !== CAPTURE_SAMPLE_RATE) {
              this.callbacks.onLog(
                `browser gave a ${notice.contextRate}Hz audio context ` +
                  `(asked for ${CAPTURE_SAMPLE_RATE}Hz); resampling to ` +
                  `${TARGET_SAMPLE_RATE}Hz`,
                'info',
              );
            }
          }
          return;
        }

        this.lastFrameAt = Date.now();
        if (!seen) {
          seen = true;
          window.clearTimeout(timer);
          resolve();
        }
        if (socket.readyState !== WebSocket.OPEN) return;
        // Realtime audio: a frame delivered late is useless, so a stalled
        // socket drops frames instead of accumulating them.
        if (socket.bufferedAmount > MAX_BUFFERED_BYTES) {
          this.droppedForBackpressure += 1;
          if (this.droppedForBackpressure === 1) {
            this.callbacks.onLog(
              'network is behind; dropping audio frames until it catches up',
              'warn',
            );
          }
          return;
        }
        this.droppedForBackpressure = 0;
        socket.send(event.data);
      };
    });
  }

  /**
   * Subscribe to the ways the OS can take capture away underneath us.
   *
   * None of these produce an error or close the socket, so without them the
   * session sits at "on" with no audio flowing — the reported symptom.
   */
  private watchHardware(stream: MediaStream, context: AudioContext): void {
    for (const track of stream.getAudioTracks?.() ?? []) {
      // Device unplugged, Bluetooth dropped, or the browser revoked it.
      track.onended = () => {
        if (this.stream !== stream) return;
        this.callbacks.onLog('the input device disconnected', 'warn');
        this.faulted('input device ended');
      };
      // Another application took exclusive control. The track stays live and
      // delivers silence, so this is reported rather than reconnected — a
      // reconnect would just grab the same muted device.
      track.onmute = () => {
        if (this.stream !== stream) return;
        this.callbacks.onLog(
          'the input was muted by the system; Throughline is hearing silence',
          'warn',
        );
      };
      track.onunmute = () => {
        if (this.stream !== stream) return;
        this.callbacks.onLog('the input came back', 'ok');
      };
    }

    // Backgrounding a tab or an OS audio interruption suspends the context.
    // The worklet stops being pulled and frames simply cease.
    context.onstatechange = () => {
      if (this.context !== context) return;
      if (context.state === 'suspended') {
        this.callbacks.onLog('audio was suspended; resuming', 'warn');
        void context.resume().catch(() => {
          this.faulted('audio context could not be resumed');
        });
      }
    };
  }

  /**
   * The catch-all. Whatever the cause, if no frame has been produced in
   * `frameWatchdogMs` then capture is not working, and that is worth acting
   * on even when every individual signal looks healthy.
   */
  private startWatchdog(): void {
    this.clearWatchdog();
    this.lastFrameAt = Date.now();
    const interval = Math.max(20, (this.deps.frameWatchdogMs ?? FRAME_WATCHDOG_MS) / 2);

    this.watchdogTimer = window.setInterval(() => {
      if (this.state !== 'on') return;
      const limit = this.deps.frameWatchdogMs ?? FRAME_WATCHDOG_MS;
      const since = Date.now() - this.lastFrameAt;
      if (since < limit) return;

      // A suspended context is the one cause with a cheap in-place fix, so
      // try that before tearing the whole pipeline down.
      if (this.context && this.context.state === 'suspended') {
        this.callbacks.onLog('audio stopped while suspended; resuming', 'warn');
        void this.context.resume().catch(() => undefined);
        this.lastFrameAt = Date.now();
        return;
      }

      this.callbacks.onLog(
        `audio stopped arriving ${Math.round(since / 1000)}s ago; reconnecting`,
        'warn',
      );
      this.faulted('no audio frames');
    }, interval);
  }

  private clearWatchdog(): void {
    if (this.watchdogTimer !== undefined) {
      window.clearInterval(this.watchdogTimer);
      this.watchdogTimer = undefined;
    }
  }

  /** Tear down the current pipeline and reconnect if the GM still wants it. */
  private faulted(reason: string): void {
    this.clearWatchdog();
    const { socket, context, stream } = this;
    this.socket = null;
    this.context = null;
    this.stream = null;
    void disposeAll(socket, context, stream);
    this.setState('off');
    if (this.desired) this.scheduleReconnect(reason);
  }

  private scheduleReconnect(reason: string): void {
    if (!this.desired || this.reconnectTimer !== undefined) return;
    const base = this.deps.reconnectBaseMs ?? 1_000;
    const delay = Math.min(base * 2 ** this.reconnectAttempt, MAX_RECONNECT_MS);
    this.reconnectAttempt += 1;
    this.setState('starting');
    this.callbacks.onLog(
      `mic disconnected; reconnecting in ${(delay / 1000).toFixed(1)}s` +
        (reason ? ` (${reason})` : ''),
      'warn',
    );
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      if (!this.desired) return;
      void this.start({ reconnect: true });
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== undefined) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }
}

async function disposeAll(
  socket: WebSocket | null,
  context: AudioContext | null,
  stream: MediaStream | null,
): Promise<void> {
  try {
    if (socket && socket.readyState < WebSocket.CLOSING) {
      // Drop the close handler first so teardown doesn't look like a drop
      // and trigger a reconnect.
      socket.onclose = null;
      socket.close();
    }
  } catch {
    /* already gone */
  }
  try {
    if (context && context.state !== 'closed') await context.close();
  } catch {
    /* already gone */
  }
  stream?.getTracks().forEach((track) => track.stop());
}
